<template>
  <ValidationObserver rules="required" v-slot="{ handleSubmit }">
    <div class="w-full h-full pt-2">
      <div class="flex">
        <back-button label="Back" @onClick="
          $router.push({
            name: 'EmployeeDirectory',
          })
          " variant="secondary" class="mr-4" />
        <div class="mt-4" style="border-left: 1px solid #878e99; height: 1.5rem" />
        <h1 class="text-xl text-left font-extrabold ml-4 mt-3 mr-8">
          Create Employee
        </h1>
      </div>
      <div style="height: 100%" v-if="loading">
        <loader size="xxs" :loader-image="false" />
      </div>
      <card v-else class="mx-3 p-5 mt-1 mb-4">
        <div class="flex">
          <p class="flex-grow text-lg font-bold">Personal Information</p>
          <div class="flex">
            <card class="w-10 text-xl mr-5 font-extrabold h-10">
              <p class="text-center my-2">1</p>
            </card>
            <div class="flex mt-2" :class="{ 'cursor-pointer': $route.params.id || allowNextPage }"
              @click="handleSwitch">
              <div class="font-10 mr-4">
                <p class="text-flame">Next</p>
                <p class="uppercase font-semibold">Employment Details</p>
              </div>
              <Icon size="xs" icon-name="icon-arrow-right" />
            </div>
          </div>
        </div>
        <image-upload @file="employeeBasic.basic.photo = $event" :photo="employeeBasic.basic.photo" :url="url"
          :name="`${employeeBasic.basic.fname} ${employeeBasic.basic.lname}`" />
        <div class="mt-6">
          <p class="font-semibold mb-7">Bio Data</p>
          <div class="grid grid-cols-12 gap-6 text-darkPurple" style="margin-top: 1.125rem">
            <div class="col-span-3">
              <c-text placeholder="--Enter--" variant="w-full" :rules="['required']" label="First Name"
                v-model="employeeBasic.basic.fname" />
            </div>
            <div class="col-span-3">
              <c-text placeholder="--Enter--" variant="w-full" label="Middle Name"
                v-model="employeeBasic.basic.mname" />
            </div>
            <div class="col-span-3">
              <c-text placeholder="--Enter--" :rules="['required']" variant="w-full h-10" label="Last Name"
                v-model="employeeBasic.basic.lname" />
            </div>
            <div class="col-span-3">
              <c-select placeholder="--Enter--" variant="w-full h-10" :options="gender" label="Gender"
                v-model="employeeBasic.basic.gender" />
            </div>
            <div class="col-span-3">
              <label class="date-label">Date of Birth</label>
              <div class="date-flex">
                <datepicker placeholder="--Enter--" input-class="date-input" type="date" required
                  style="width: 100%; outline: none" v-model="employeeBasic.basic.dob" />
              </div>
            </div>
            <div class="col-span-3">
              <c-select placeholder="--Enter--" variant="w-full" :options="countrySelect" @change="changeState"
                label="Nationality" v-model="employeeBasic.basic.nationality" />
            </div>
            <div class="col-span-3">
              <c-select placeholder="--Enter--" v-if="employeeBasic.basic.nationality === 'NGA'" @onChange="changeLGA"
                variant="w-full" label="State" :options="$store.state.statesOrigin"
                v-model="employeeBasic.basic.stateOfOrigin" />
              <c-text placeholder="--Enter--" v-else variant="w-full h-10" label="State"
                v-model="employeeBasic.basic.stateOfOrigin" />
            </div>
            <div class="col-span-3">
              <!-- <c-select placeholder="--Enter--" v-if="employeeBasic.basic.nationality === 'NGA'"
                variant="w-full" label="Local Government Area" :options="lgaArray"
                v-model="employeeBasic.basic.localGovernmentArea" /> -->
              <c-text placeholder="--Enter--" variant="w-full" label="Local Government Area"
                v-model="employeeBasic.basic.localGovernmentArea" />
            </div>
            <div class="col-span-3">
              <c-select placeholder="--Enter--" variant="w-full h-10" label="Marital Status"
                :options="maritalStatusOptions" v-model="employeeBasic.basic.maritalStatus" />
            </div>
            <div class="col-span-3">
              <c-select placeholder="--Enter--" variant="w-full h-10" label="ID Type" :options="idType"
                v-model="employeeBasic.basic.idType" />
            </div>
            <div class="col-span-3">
              <c-text placeholder="--Enter--" variant="w-full" label="ID Number"
                v-model="employeeBasic.basic.idNumber" />
            </div>
          </div>
          <divider class="my-8" />
          <p class="font-semibold mb-7">Contact Info</p>
          <div class="grid grid-cols-12 gap-6 text-darkPurple" style="margin-top: 1.125rem">
            <div class="col-span-3">
              <c-select placeholder="--Enter--" variant="w-full" :options="countrySelect" @change="changeState"
                label="Country" v-model="employeeBasic.basic.country" />
            </div>
            <div class="col-span-3">
              <c-select placeholder="--Enter--" v-if="employeeBasic.basic.country === 'NGA'" variant="w-full h-10"
                label="State" :options="$store.state.states" v-model="employeeBasic.basic.state" />
              <c-text placeholder="--Enter--" v-else variant="w-full h-10" label="State"
                v-model="employeeBasic.basic.state" />
            </div>
            <div class="col-span-3">
              <c-text placeholder="--Enter--" variant="w-full h-10" label="City" v-model="employeeBasic.basic.city" />
            </div>
            <div class="col-span-3">
              <c-text placeholder="--Enter--" variant="w-full" label="Address" v-model="employeeBasic.basic.address" />
            </div>
            <div class="col-span-3">
              <c-text placeholder="--Enter--" variant="w-full h-10" label="Unit" v-model="employeeBasic.basic.unit" />
            </div>
            <div class="col-span-3">
              <c-text placeholder="--Enter--" variant="w-full h-10" label="Postal Code" type="number"
                v-model="employeeBasic.basic.zip" />
            </div>
            <div class="col-span-3">
              <c-text placeholder="--Enter--" variant="w-full h-10" type="number" label="Phone Number"
                v-model="employeeBasic.basic.phoneNo" />
            </div>
            <div class="col-span-3">
              <c-text placeholder="--Enter--" variant="w-full" label="Work Email" :rules="EmailRule" v-model="email" />
            </div>
            <div class="col-span-3">
              <c-text placeholder="--Enter--" variant="w-full" label="Personal Email"
                v-model="employeeBasic.basic.alternateEmail" />
            </div>
          </div>
          <divider class="my-8" />
          <div class="w-full flex justify-between items-center">
            <p class="font-semibold">Next of Kin</p>
            <div class="cursor-pointer" @click="isNextOfKin = !isNextOfKin">
              <icon :icon-name="isNextOfKin ? 'chevronUp' : 'icon-arrow-down'" class="icon" size="xs"/>
            </div>
          </div>
          <div v-if="isNextOfKin" class="grid grid-cols-12 gap-6 text-darkPurple" style="margin-top: 1.125rem">
            <div class="col-span-3">
              <c-text placeholder="--Enter--" variant="w-full" label="First Name"
                v-model="employeeBasic.basic.nokFname" />
            </div>
            <div class="col-span-3">
              <c-text placeholder="--Enter--" variant="w-full" label="Last Name"
                v-model="employeeBasic.basic.nokLname" />
            </div>
            <div class="col-span-3">
              <c-text placeholder="--Enter--" variant="w-full h-10" label="Phone Number"
                v-model="employeeBasic.basic.nokPhone" />
            </div>
            <div class="col-span-3">
              <c-text placeholder="--Enter--" variant="w-full h-10" label="Email"
                v-model="employeeBasic.basic.nokEmail" />
            </div>
            <div class="col-span-3">
              <c-select placeholder="--Enter--" variant="w-full h-10" :options="$store.state.nok" label="Relationship"
                v-model="employeeBasic.basic.nokRelationship" />
            </div>
            <div class="col-span-3">
              <c-text placeholder="--Enter--" variant="w-full h-10" label="Address"
                v-model="employeeBasic.basic.nokAddress" />
            </div>
            <!-- <div class="col-span-12">
              <button @click="clearNextOfKin" type="button" class="text-red-600 hover:text-red-800 text-sm">
                Clear Next of Kin Information
              </button>
            </div> -->
          </div>
          <divider class="my-8" />
          <div class="w-full flex justify-between items-center">
            <p class="font-semibold">Beneficiary</p>
            <div class="cursor-pointer" @click="isBeneficiary = !isBeneficiary">
              <icon :icon-name="isBeneficiary ? 'chevronUp' : 'icon-arrow-down'" class="icon" size="xs"/>
            </div>
          </div>
          <div class="my-1 mb-3">
            <checkbox label="Same as Next of Kin" v-model="beneficiarySameAsNok"
              checkbox-size="height:16px; width:16px; margin-top:4px; cursor: pointer;" label-style="color:#333333"
            />
          </div>
          <div v-if="isBeneficiary" class="grid grid-cols-12 gap-6 text-darkPurple" style="margin-top: 1.125rem">
            <div class="col-span-3">
              <c-text placeholder="--Enter--" variant="w-full" label="First Name" v-model="beneficiaryData.beneficiaryFname"
                :disabled="beneficiarySameAsNok" />
            </div>
            <div class="col-span-3">
              <c-text placeholder="--Enter--" variant="w-full" label="Last Name" v-model="beneficiaryData.beneficiaryLname"
                :disabled="beneficiarySameAsNok" />
            </div>
            <div class="col-span-3">
              <c-text placeholder="--Enter--" variant="w-full h-10" label="Phone Number" v-model="beneficiaryData.beneficiaryPhone"
                :disabled="beneficiarySameAsNok" />
            </div>
            <div class="col-span-3">
              <c-text placeholder="--Enter--" variant="w-full h-10" label="Email" v-model="beneficiaryData.beneficiaryEmail"
                :disabled="beneficiarySameAsNok" />
            </div>
            <div class="col-span-3">
              <c-select placeholder="--Enter--" variant="w-full h-10" :options="$store.state.nok" label="Relationship"
                v-model="beneficiaryData.beneficiaryRelationship" :disabled="beneficiarySameAsNok" />
            </div>
            <div class="col-span-3">
              <c-text placeholder="--Enter--" variant="w-full h-10" label="Address" v-model="beneficiaryData.beneficiaryAddress"
                :disabled="beneficiarySameAsNok" />
            </div>
            <!-- <div class="col-span-12">
              <button @click="clearBeneficiary" type="button" class="text-red-600 hover:text-red-800 text-sm">
                Clear Beneficiary Information
              </button>
            </div> -->
          </div>
          <divider class="my-8" />
          <div class="w-full flex justify-between items-center">
            <p class="font-semibold">Emergency contact</p>
            <div class="cursor-pointer" @click="isEmergencyContact = !isEmergencyContact">
              <icon :icon-name="isEmergencyContact ? 'chevronUp' : 'icon-arrow-down'" class="icon" size="xs"/>
            </div>
          </div>
          <div class="my-1 mb-3">
            <checkbox label="Same as Next of Kin" v-model="emergencyContactSameAsNok"
              checkbox-size="height:16px; width:16px; margin-top:4px; cursor: pointer;" label-style="color:#333333"
            />
          </div>
          <div v-if="isEmergencyContact" class="grid grid-cols-12 gap-6 text-darkPurple" style="margin-top: 1.125rem">
            <div class="col-span-3">
              <c-text placeholder="--Enter--" variant="w-full" label="First Name" v-model="emergencyContactData.emergencyContactFname"
                :disabled="emergencyContactSameAsNok" />
            </div>
            <div class="col-span-3">
              <c-text placeholder="--Enter--" variant="w-full" label="Last Name" v-model="emergencyContactData.emergencyContactLname"
                :disabled="emergencyContactSameAsNok" />
            </div>
            <div class="col-span-3">
              <c-text placeholder="--Enter--" variant="w-full h-10" label="Phone Number" v-model="emergencyContactData.emergencyContactPhone"
                :disabled="emergencyContactSameAsNok" />
            </div>
            <div class="col-span-3">
              <c-text placeholder="--Enter--" variant="w-full h-10" label="Email" v-model="emergencyContactData.emergencyContactEmail"
                :disabled="emergencyContactSameAsNok" />
            </div>
            <div class="col-span-3">
              <c-select placeholder="--Enter--" variant="w-full h-10" :options="$store.state.nok" label="Relationship"
                v-model="emergencyContactData.emergencyContactRelationship" :disabled="emergencyContactSameAsNok" />
            </div>
            <div class="col-span-3">
              <c-text placeholder="--Enter--" variant="w-full h-10" label="Address" v-model="emergencyContactData.emergencyContactAddress"
                :disabled="emergencyContactSameAsNok" />
            </div>
            <!-- <div class="col-span-12">
              <button @click="clearEmergency" type="button" class="text-red-600 hover:text-red-800 text-sm">
                Clear Emergency Contact Information
              </button>
            </div> -->
          </div>
          <divider class="my-8" />
          <div class="mb-6">
            <p class="text-darkPurple text-base mb-4">
              Generate and share credentials with employee
            </p>
            <RadioButton :options="credentialOptions" col-span="col-span-1 " class="ml-2 text-base text-darkPurple"
              v-model="employeeBasic.basic.canLogin" space-between="mr-2" />
            <small v-if="!employeeBasic.basic.canLogin" class="text-flame">Please select one</small>
          </div>
          <divider class="my-8" />
          <div class="mb-6" v-feature-request>
            <checkbox label="Allow employee updates" class="cursor-pointer"
              checkbox-size="height:16px; width:16px; margin-top:4px;" label-style="color:#333333"
              v-model="employeeBasic.basic.allowEmployeeUpdates" />
          </div>
          <div class="flex mt-4">
            <div class="flex flex-grow">
              <Button width="7.875rem" @click="handleSubmit(handleSaveNow)" :class="`text-white buttons`"
                :background="appearance.buttonColor || defaultBTNColor" :disabled="disableBtn">
                Next
              </Button>
              <Button width="7.875rem" @click="$router.push({ name: 'EmployeeDirectory' })" class="buttons">
                Cancel
              </Button>
            </div>
            <div>
              <Button @click="handleSubmit(handleSaveLater)" :background="appearance.buttonColor || defaultBTNColor"
                class="text-white buttons">
                Save & Finish later
              </Button>
            </div>
          </div>
        </div>
      </card>
    </div>
  </ValidationObserver>
</template>

<script>
import { mapState } from "vuex";
import { ValidationObserver } from "vee-validate";
import CText from "@scelloo/cloudenly-ui/src/components/text";
import CSelect from "@scelloo/cloudenly-ui/src/components/select";
import BackButton from "@scelloo/cloudenly-ui/src/components/back-button";
import Datepicker from "vuejs-datepicker";
import ImageUpload from "@/components/imageUpload";
import gender from "@/helpers/gender";
import Card from "@/components/Card";
import RadioButton from "@/components/RadioButton";
import Divider from "@/components/divider";
import Button from "@/components/Button";
import Checkbox from "@/components/Checkbox";

export default {
  name: "PersonalInfo",
  components: {
    ValidationObserver,
    Card,
    ImageUpload,
    CText,
    BackButton,
    CSelect,
    Datepicker,
    Checkbox,
    RadioButton,
    Button,
    Divider,
  },

  data() {
    return {
      disableBtn: false,
      gender,
      photo: "",
      email: "",
      error: [],
      timeout: null,
      url: process.env.VUE_APP_IMAGE_UPLOAD,
      countrySelect: [],
      allowNextPage: false,
      loading: false,
      pendingImageFile: null,
      uploading: false,
      isNextOfKin: true,
      isBeneficiary: true,
      isEmergencyContact: true,
      beneficiarySameAsNok: false,
      emergencyContactSameAsNok: false,
      idType: [
        {
          id: "NationalId",
          name: "National ID",
        },
        {
          id: "InternationalPassportID",
          name: "International Passport ID",
        },
        {
          id: "DriversLicenseID",
          name: "Drivers License ID",
        },
      ],
      credentialOptions: [
        { name: "Yes", radioName: "credential", value: true },
        { name: "No", radioName: "credential", value: false },
      ],
      maritalStatusOptions: [
        { id: "Single", name: "Single" },
        { id: "Married", name: "Married" },
        { id: "Separated", name: "Separated" },
        { id: "Divorced", name: "Divorced" },
        { id: "Widowed", name: "Widowed" },
        { id: "In a Relationship", name: "In a Relationship" },
        { id: "Engaged", name: "Engaged" },
        { id: "Domestic Partnership", name: "Domestic Partnership" },
        { id: "Civil Union", name: "Civil Union" },
        { id: "Prefer not to say", name: "Prefer not to say" }
      ],
      EmailRule: [
        "required",
        {
          name: "email",
          rule: (v) => /.+@.+\..+/.test(v) || "E-mail must be valid",
        },
        {
          name: "available",
          rule: (v) => this.handleEmail(v),
        },
      ],
      showStates: true,
      showCredentialError: false,
      lgaArray: [],
      employeeBasic: {
        basic: {
          photo: null,
          fname: "",
          lname: "",
          mname: "",
          dob: null,
          gender: "",
          email: "",
          alternateEmail: "",
          phoneNo: "",
          alternatePhoneNo: "",
          nokFname: "",
          nokLname: "",
          nokMname: "",
          nokEmail: "",
          nokAddress: "",
          nokRelationship: "",
          nokPhone: "",
          address: "",
          country: "",
          canLogin: null,
          state: "",
          city: "",
          zip: "",
          iso2: "",
          unit: "",
          dialCode: "",
          stateOfOrigin: "",
          stateOfOriginId: "",
          localGovernmentArea: null,
          idType: "",
          idNumber: "",
          allowEmployeeUpdates: false,
        },
      },
      // Pre-define beneficiary and emergency contact properties
      beneficiaryData: {
        beneficiaryFname: '',
        beneficiaryLname: '',
        beneficiaryPhone: '',
        beneficiaryEmail: '',
        beneficiaryRelationship: '',
        beneficiaryAddress: '',
        beneficiaryIsNextOfKin: false
      },
      emergencyContactData: {
        emergencyContactFname: '',
        emergencyContactLname: '',
        emergencyContactPhone: '',
        emergencyContactEmail: '',
        emergencyContactRelationship: '',
        emergencyContactAddress: '',
        emergencyContactIsNextOfKin: false
      }
    };
  },

  computed: {
    ...mapState({
      defaultBTNColor: (state) => state.orgAppearance.defaultBtnColor,
      appearance: (state) => state.orgAppearance.orgAppearanceData,
    }),
  },
  watch: {
    beneficiarySameAsNok(newValue) {
      if (newValue) {
        this.beneficiaryData.beneficiaryFname = this.employeeBasic.basic.nokFname || '';
        this.beneficiaryData.beneficiaryLname = this.employeeBasic.basic.nokLname || '';
        this.beneficiaryData.beneficiaryPhone = this.employeeBasic.basic.nokPhone || '';
        this.beneficiaryData.beneficiaryEmail = this.employeeBasic.basic.nokEmail || '';
        this.beneficiaryData.beneficiaryRelationship = this.employeeBasic.basic.nokRelationship || '';
        this.beneficiaryData.beneficiaryAddress = this.employeeBasic.basic.nokAddress || '';
        this.beneficiaryData.beneficiaryIsNextOfKin = true;
      } else {
        this.beneficiaryData.beneficiaryFname = '';
        this.beneficiaryData.beneficiaryLname = '';
        this.beneficiaryData.beneficiaryPhone = '';
        this.beneficiaryData.beneficiaryEmail = '';
        this.beneficiaryData.beneficiaryRelationship = '';
        this.beneficiaryData.beneficiaryAddress = '';
        this.beneficiaryData.beneficiaryIsNextOfKin = false;
      }
    },
    emergencyContactSameAsNok(newValue) {
      if (newValue) {
        this.emergencyContactData.emergencyContactFname = this.employeeBasic.basic.nokFname || '';
        this.emergencyContactData.emergencyContactLname = this.employeeBasic.basic.nokLname || '';
        this.emergencyContactData.emergencyContactPhone = this.employeeBasic.basic.nokPhone || '';
        this.emergencyContactData.emergencyContactEmail = this.employeeBasic.basic.nokEmail || '';
        this.emergencyContactData.emergencyContactRelationship = this.employeeBasic.basic.nokRelationship || '';
        this.emergencyContactData.emergencyContactAddress = this.employeeBasic.basic.nokAddress || '';
        this.emergencyContactData.emergencyContactIsNextOfKin = true;
      } else {
        this.emergencyContactData.emergencyContactFname = '';
        this.emergencyContactData.emergencyContactLname = '';
        this.emergencyContactData.emergencyContactPhone = '';
        this.emergencyContactData.emergencyContactEmail = '';
        this.emergencyContactData.emergencyContactRelationship = '';
        this.emergencyContactData.emergencyContactAddress = '';
        this.emergencyContactData.emergencyContactIsNextOfKin = false;
      }
    },
    'employeeBasic.basic.nokFname': {
      handler() { this.syncFromNok(); }
    },
    'employeeBasic.basic.nokLname': {
      handler() { this.syncFromNok(); }
    },
    'employeeBasic.basic.nokPhone': {
      handler() { this.syncFromNok(); }
    },
    'employeeBasic.basic.nokEmail': {
      handler() { this.syncFromNok(); }
    },
    'employeeBasic.basic.nokRelationship': {
      handler() { this.syncFromNok(); }
    },
    'employeeBasic.basic.nokAddress': {
      handler() { this.syncFromNok(); }
    },
  },

  methods: {
    async handleEmail(value) {
      const result = await this.$_checkEmailExist(value);
      if (result.data.emailExists) {
        return "Email already exists";
      }
      return true;
    },
    handleSave() {
      this.employeeBasic.basic.containsBio = true;
      this.$_editOneEmployee({
        id: this.$route.params.id,
        employeeData: this.employeeBasic.basic,
      });
    },
    handleSaveNow() {
      this.createEmployees("next");
    },
    handleSaveLater() {
      this.createEmployees("later");
    },
    convertValue(value) {
      if (value === "true" || value === true) {
        return true;
      }
      return false;
    },
    async createEmployees(type) {
      if (this.employeeBasic.basic.canLogin === null) {
        this.showCredentialError = true;
        this.$toasted.error("Please select if employee can get credentials", {
          duration: 3000,
        });
        return;
      }
      this.employeeBasic.basic.canLogin = this.convertValue(
        this.employeeBasic.basic.canLogin
      );
      this.disableBtn = true;
      this.employeeBasic.basic.createdBy = this.$AuthUser.id;
      this.employeeBasic.basic.orgId = this.$AuthUser.orgId;
      this.employeeBasic.basic.email = this.email;
      this.employeeBasic.basic.dob = this.employeeBasic.basic.dob
        ? this.employeeBasic.basic.dob.toString()
        : null;
      this.employeeBasic.basic.containsBio = true;
      // Upload image first if there's a pending file
      if (this.pendingImageFile) {
        try {
          this.uploading = true;
          const imageUrl = await this.$refs.imageUpload.uploadImageToServer();
          // Update employee data with the uploaded image URL
          this.employeeBasic.basic.photo = imageUrl;
        } catch (err) {
          this.uploading = false;
          this.$toasted.error("Image upload failed. Please try again.", {
            duration: 3000,
          });
          return;
        }
      }
      // Merge beneficiary and emergency contact data into employeeBasic.basic
      this.employeeBasic.basic = {
        ...this.employeeBasic.basic,
        ...this.beneficiaryData,
        ...this.emergencyContactData,
        beneficiaryIsNextOfKin: this.beneficiarySameAsNok,
        emergencyContactIsNextOfKin: this.emergencyContactSameAsNok
      };
      localStorage.setItem('employeeInfo', this.employeeBasic);
      this.$_createEmployee(this.employeeBasic)
        .then((res) => {
          if (type === "next") {
            this.$router.push({
              name: "EditPersonalInfo",
              params: {
                id: res.data.userId,
              },
              query: {
                currentTab: "Education & Certification",
                slug: 'new',
                user: `${this.employeeBasic.basic.fname} ${this.employeeBasic.basic.lname}`,
              },
            });
          } else {
            this.$router.push({ name: "EmployeeDirectory" });
          }
          this.creatingEmployee = false;
          this.disableBtn = false;
        })
        .catch((err) => {
          this.$toasted.error("Employee not created successfully", {
            duration: 3000,
          });
          this.disableBtn = false;
          throw new Error(err);
        });
    },
    changeState(value) {
      if (value === "NGA") {
        this.showStates = true;
      } else this.showStates = false;
    },
    changeLGA(value) {
      this.lgaArray = [];
      this.$_getLocalGovernmentArears(value).then(({ data }) => {
        this.lgaArray = data.data.map((element) => ({
          id: element.id,
          name: element.area,
        }));
      }).catch(() => {
        this.$toasted.error("Failed to fetch local government areas", {
          duration: 3000,
        });
      });
    },
    handleSwitch() {
      if (this.$route.params.id || this.allowNextPage) {
        this.$router.push({
          name: "EditEmploymentDetails",
          params: { id: this.$route.params.id },
        });
      }
    },
    getPersonalInfo() {
      this.$_getOneEmployeeAll({
        id: this.$route.params.id,
      }).then(({ data }) => {
        this.employeeBasic.basic = data;
        this.email = this.employeeBasic.basic.email;
        if (this.employeeBasic.basic.state) {
          this.$store.state.states.find((element) => {
            if (element.id === this.employeeBasic.basic.state) {
              this.employeeBasic.basic.state = element.name;
            }
            return this.employeeBasic.basic.state;
          });
        }
        this.loading = false;
      });
    },
    editAnnualGross() {
      this.isDisabled = false;
      this.isEdit = false;
    },
    saveAnnualGross() {
      this.isDisabled = true;
      this.isEdit = true;
    },
    clearNextOfKin() {
      this.employeeBasic.basic.nokFname = '';
      this.employeeBasic.basic.nokLname = '';
      this.employeeBasic.basic.nokPhone = '';
      this.employeeBasic.basic.nokEmail = '';
      this.employeeBasic.basic.nokRelationship = '';
      this.employeeBasic.basic.nokAddress = '';

      // Clear beneficiary and emergency contact if they were synced
      if (this.beneficiarySameAsNok) {
        this.beneficiaryData.beneficiaryFname = '';
        this.beneficiaryData.beneficiaryLname = '';
        this.beneficiaryData.beneficiaryPhone = '';
        this.beneficiaryData.beneficiaryEmail = '';
        this.beneficiaryData.beneficiaryRelationship = '';
        this.beneficiaryData.beneficiaryAddress = '';
        this.beneficiaryData.beneficiaryIsNextOfKin = false;
        this.beneficiarySameAsNok = false;
      }

      if (this.emergencyContactSameAsNok) {
        this.emergencyContactData.emergencyContactFname = '';
        this.emergencyContactData.emergencyContactLname = '';
        this.emergencyContactData.emergencyContactPhone = '';
        this.emergencyContactData.emergencyContactEmail = '';
        this.emergencyContactData.emergencyContactRelationship = '';
        this.emergencyContactData.emergencyContactAddress = '';
        this.emergencyContactData.emergencyContactIsNextOfKin = false;
        this.emergencyContactSameAsNok = false;
      }
    },
    clearBeneficiary() {
      this.beneficiaryData.beneficiaryFname = '';
      this.beneficiaryData.beneficiaryLname = '';
      this.beneficiaryData.beneficiaryPhone = '';
      this.beneficiaryData.beneficiaryEmail = '';
      this.beneficiaryData.beneficiaryRelationship = '';
      this.beneficiaryData.beneficiaryAddress = '';
      this.beneficiaryData.beneficiaryIsNextOfKin = false;
      this.beneficiarySameAsNok = false;
    },
    clearEmergency() {
      this.emergencyContactData.emergencyContactFname = '';
      this.emergencyContactData.emergencyContactLname = '';
      this.emergencyContactData.emergencyContactPhone = '';
      this.emergencyContactData.emergencyContactEmail = '';
      this.emergencyContactData.emergencyContactRelationship = '';
      this.emergencyContactData.emergencyContactAddress = '';
      this.emergencyContactData.emergencyContactIsNextOfKin = false;
      this.emergencyContactSameAsNok = false;
    },
    syncFromNok() {
      if (this.beneficiarySameAsNok) {
        this.beneficiaryData.beneficiaryFname = this.employeeBasic.basic.nokFname || '';
        this.beneficiaryData.beneficiaryLname = this.employeeBasic.basic.nokLname || '';
        this.beneficiaryData.beneficiaryPhone = this.employeeBasic.basic.nokPhone || '';
        this.beneficiaryData.beneficiaryEmail = this.employeeBasic.basic.nokEmail || '';
        this.beneficiaryData.beneficiaryRelationship = this.employeeBasic.basic.nokRelationship || '';
        this.beneficiaryData.beneficiaryAddress = this.employeeBasic.basic.nokAddress || '';
      }

      // Sync emergency contact if checkbox is checked
      if (this.emergencyContactSameAsNok) {
        this.emergencyContactData.emergencyContactFname = this.employeeBasic.basic.nokFname || '';
        this.emergencyContactData.emergencyContactLname = this.employeeBasic.basic.nokLname || '';
        this.emergencyContactData.emergencyContactPhone = this.employeeBasic.basic.nokPhone || '';
        this.emergencyContactData.emergencyContactEmail = this.employeeBasic.basic.nokEmail || '';
        this.emergencyContactData.emergencyContactRelationship = this.employeeBasic.basic.nokRelationship || '';
        this.emergencyContactData.emergencyContactAddress = this.employeeBasic.basic.nokAddress || '';
      }
    },
  },
  mounted() {
    this.countrySelect = this.$store.state.countries.map((element) => ({
      id: element.Alpha3Code,
      name: element.Name,
    }));
    if (this.$route.params.id) {
      this.loading = true;
      this.getPersonalInfo();
    }
  },
};
</script>

<style scoped>
.icon{
  width: 20px !important;
  height: 15px !important;
}
</style>
